import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Table, Button, Select, Space, Typography, Card, Statistic,
    message, Popconfirm, Tag, Row, Col, Segmented, Input,
} from 'antd';
import {
    MailOutlined, ReloadOutlined, UserOutlined,
    FileTextOutlined, DollarOutlined, SearchOutlined,
} from '@ant-design/icons';
import AdminLayout from '../Layout/AdminLayout';
import axiosInstance from '../../utils/axiosConfig';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Search } = Input;

const CRITERIA = [
    {
        value: 'personal_info',
        label: 'Thông tin cá nhân',
        icon: <UserOutlined />,
        description: 'Thí sinh chưa điền đủ các trường bắt buộc',
        color: '#fa8c16',
        emailSubject: '[UTT] Nhắc nhở: Chưa hoàn thiện thông tin cá nhân',
    },
    {
        value: 'evidence',
        label: 'Minh chứng',
        icon: <FileTextOutlined />,
        description: 'Thiếu học bạ THPT hoặc CCCD 2 mặt',
        color: '#1677ff',
        emailSubject: '[UTT] Nhắc nhở: Chưa nộp đủ minh chứng (Học bạ + CCCD 2 mặt)',
    },
    {
        value: 'payment',
        label: 'Lệ phí',
        icon: <DollarOutlined />,
        description: 'Thí sinh chưa thanh toán lệ phí xét tuyển',
        color: '#52c41a',
        emailSubject: '[UTT] Nhắc nhở: Chưa thanh toán lệ phí xét tuyển',
    },
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2].map(
    (y) => ({ value: y, label: String(y) })
);

const STATUS_TAG = {
    draft:     { color: 'default',    label: 'Chưa hoàn thiện' },
    submitted: { color: 'processing', label: 'Chờ thanh toán' },
    completed: { color: 'success',    label: 'Hoàn thành' },
};

const fmtDate = (v) => v && dayjs(v).isValid() ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—';

const BASE_COLUMNS = [
    { title: 'Mã hồ sơ', dataIndex: 'student_code', width: 110 },
    { title: 'Họ tên',   dataIndex: 'full_name',     ellipsis: true },
    { title: 'Email',    dataIndex: 'email',          ellipsis: true },
    { title: 'SĐT',      dataIndex: 'phone',          width: 130 },
    // {
    //     title: 'Năm TN',
    //     dataIndex: 'graduation_year',
    //     width: 85,
    //     render: (y) => y ? <Tag color="blue">{y}</Tag> : <Text type="secondary">—</Text>,
    // },
    {
        title: 'Ngày đăng ký',
        dataIndex: 'registered_at',
        width: 145,
        render: fmtDate,
        sorter: (a, b) => (a.registered_at ?? '').localeCompare(b.registered_at ?? ''),
        defaultSortOrder: 'descend',
    },
    {
        title: 'Ngày xác thực',
        dataIndex: 'verified_at',
        width: 145,
        render: fmtDate,
        sorter: (a, b) => (a.verified_at ?? '').localeCompare(b.verified_at ?? ''),
    },
    {
        title: 'Trạng thái hồ sơ',
        dataIndex: 'status',
        width: 145,
        render: (s) => {
            const cfg = STATUS_TAG[s] || STATUS_TAG.draft;
            return <Tag color={cfg.color}>{cfg.label}</Tag>;
        },
    },
];

const EmailReminders = () => {
    const [criterion, setCriterion]       = useState('personal_info');
    const [graduationYear, setGYear]      = useState(null);
    const [students, setStudents]         = useState([]);
    const [loading, setLoading]           = useState(false);
    const [sending, setSending]           = useState(false);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [searchText, setSearchText]     = useState('');

    const activeCriteria = CRITERIA.find((c) => c.value === criterion);

    const fetchStudents = useCallback(async () => {
        setLoading(true);
        setSelectedKeys([]);
        setSearchText('');
        try {
            const params = { criterion };
            if (criterion === 'evidence' && graduationYear) params.graduation_year = graduationYear;
            const { data } = await axiosInstance.get('/api/v1/admin/students-missing', { params });
            if (data.success) {
                setStudents(data.data.map((s) => ({ ...s, key: s.user_id })));
            }
        } catch {
            message.error('Không thể tải danh sách');
        } finally {
            setLoading(false);
        }
    }, [criterion, graduationYear]);

    useEffect(() => { fetchStudents(); }, [fetchStudents]);

    // Lọc client-side theo ô tìm kiếm
    const filteredStudents = useMemo(() => {
        if (!searchText.trim()) return students;
        const kw = searchText.toLowerCase();
        return students.filter((s) =>
            [s.full_name, s.email, s.phone, s.student_code]
                .some((v) => v?.toLowerCase().includes(kw))
        );
    }, [students, searchText]);

    const handleSend = async () => {
        if (!selectedKeys.length) return;
        setSending(true);
        try {
            const { data } = await axiosInstance.post('/api/v1/admin/students-missing/send-reminder', {
                criterion,
                user_ids: selectedKeys,
            });
            if (data.success) {
                message.success(data.message);
                setSelectedKeys([]);
            } else {
                message.error(data.message || 'Gửi thất bại');
            }
        } catch {
            message.error('Lỗi khi gửi email');
        } finally {
            setSending(false);
        }
    };

    const handleChangeCriterion = (val) => {
        setCriterion(val);
        setGYear(null);
    };

    return (
        <AdminLayout title="Gửi email nhắc nhở">
            <Row gutter={[16, 16]}>
                {/* Chọn tiêu chí */}
                <Col span={24}>
                    <Card>
                        <Space direction="vertical" style={{ width: '100%' }} size={12}>
                            <Text strong>Chọn tiêu chí nhắc nhở:</Text>
                            <Segmented
                                value={criterion}
                                onChange={handleChangeCriterion}
                                options={CRITERIA.map((c) => ({
                                    value: c.value,
                                    label: <Space>{c.icon}{c.label}</Space>,
                                }))}
                                size="large"
                            />
                            {activeCriteria && (
                                <Text type="secondary">
                                    <strong style={{ color: activeCriteria.color }}>
                                        {activeCriteria.label}
                                    </strong>
                                    {' — '}{activeCriteria.description}
                                    <br />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Mẫu email: <em>{activeCriteria.emailSubject}</em>
                                    </Text>
                                </Text>
                            )}
                        </Space>
                    </Card>
                </Col>

                {/* Thống kê + bộ lọc + nút gửi */}
                <Col span={24}>
                    <Card>
                        <Row gutter={16} align="middle" wrap>
                            <Col>
                                <Statistic
                                    title="Thí sinh cần nhắc"
                                    value={filteredStudents.length}
                                    suffix={filteredStudents.length !== students.length ? `/ ${students.length}` : undefined}
                                    prefix={<MailOutlined />}
                                    valueStyle={{ color: activeCriteria?.color }}
                                />
                            </Col>
                            <Col flex="auto">
                                <Space wrap>
                                    <Search
                                        placeholder="Tìm theo tên, email, SĐT, mã hồ sơ"
                                        allowClear
                                        style={{ width: 300 }}
                                        value={searchText}
                                        onChange={(e) => { setSearchText(e.target.value); setSelectedKeys([]); }}
                                        prefix={<SearchOutlined />}
                                    />
                                    {criterion === 'evidence' && (
                                        <Select
                                            placeholder="Lọc theo năm tốt nghiệp"
                                            allowClear
                                            style={{ width: 200 }}
                                            value={graduationYear}
                                            onChange={setGYear}
                                            options={YEAR_OPTIONS}
                                        />
                                    )}
                                    <Button
                                        icon={<ReloadOutlined />}
                                        onClick={fetchStudents}
                                        loading={loading}
                                    >
                                        Làm mới
                                    </Button>
                                </Space>
                            </Col>
                            <Col>
                                <Popconfirm
                                    title={
                                        <Space direction="vertical" size={2}>
                                            <Text strong>Xác nhận gửi email nhắc nhở?</Text>
                                            <Text type="secondary">
                                                Sẽ gửi đến <strong>{selectedKeys.length}</strong> thí sinh đã chọn
                                            </Text>
                                        </Space>
                                    }
                                    onConfirm={handleSend}
                                    okText="Gửi"
                                    cancelText="Huỷ"
                                    disabled={!selectedKeys.length}
                                >
                                    <Button
                                        type="primary"
                                        icon={<MailOutlined />}
                                        loading={sending}
                                        disabled={!selectedKeys.length}
                                    >
                                        Gửi nhắc nhở ({selectedKeys.length})
                                    </Button>
                                </Popconfirm>
                            </Col>
                        </Row>
                    </Card>
                </Col>

                {/* Bảng danh sách */}
                <Col span={24}>
                    <Card
                        title={
                            <Space>
                                {activeCriteria?.icon}
                                <Title level={5} style={{ margin: 0 }}>
                                    Danh sách thí sinh — {activeCriteria?.label}
                                </Title>
                            </Space>
                        }
                        extra={
                            selectedKeys.length > 0 && (
                                <Text type="secondary">
                                    Đã chọn <strong>{selectedKeys.length}</strong> / {filteredStudents.length}
                                </Text>
                            )
                        }
                    >
                        <Table
                            rowSelection={{
                                selectedRowKeys: selectedKeys,
                                onChange: setSelectedKeys,
                            }}
                            columns={BASE_COLUMNS}
                            dataSource={filteredStudents}
                            loading={loading}
                            pagination={{
                                pageSize: 20,
                                showSizeChanger: true,
                                showTotal: (t) => `${t} thí sinh`,
                            }}
                            scroll={{ x: 1000 }}
                            locale={{ emptyText: 'Không có thí sinh nào trong tiêu chí này' }}
                            size="small"
                        />
                    </Card>
                </Col>
            </Row>
        </AdminLayout>
    );
};

export default EmailReminders;
